import React, { useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Shield, Mail, KeyRound, ArrowRight, Loader2, Clock, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import promoVideo from "../assets/sherlock-transactions-promo.mp4";
import promoPoster from "../assets/sherlock-transactions-promo-poster.jpg";
import { loginRestrictionMessage } from "../lib/loginRestriction";

const API = "/api";

const Login = () => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeRestrictionMsg, setTimeRestrictionMsg] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Promo video — autoplays muted, with custom Unmute toggle
  const videoRef = useRef(null);
  const [videoMuted, setVideoMuted] = useState(true);
  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !videoMuted;
    videoRef.current.muted = next;
    setVideoMuted(next);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/request-otp`, { email: email.trim() });
      if (data.status === "pending") {
        setStep("pending");
      } else if (data.status === "otp_sent") {
        setStep("otp");
        toast.success(t("login.codeSentSuccess"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { email, code: otp });
      login(data.user);
      toast.success(t("login.welcomeBack"));
      navigate(data.user.role === "admin" || data.user.role === "super_admin" ? "/admin" : "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 && detail.startsWith("Login restricted.")) {
        setTimeRestrictionMsg(loginRestrictionMessage(detail, t));
        setStep("timeRestricted");
      } else {
        toast.error(detail || t("common.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#171644" }}>
      {/* Left panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 p-12"
        style={{
          background: "#171644",
        }}
      >
        <div>
          <div className="flex items-center gap-3 mb-8">
            <img
              src="/logos/logo-dark.png"
              alt="Audacia Corporate Services"
              className="h-12 object-contain"
            />
          </div>

          {/* Promo video — narrated platform tour, autoplays muted */}
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-tr from-cyan-400/20 to-transparent rounded-3xl blur-2xl pointer-events-none" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/40 ring-1 ring-white/10 bg-black">
              <video
                ref={videoRef}
                src={promoVideo}
                poster={promoPoster}
                autoPlay
                muted
                playsInline
                controls
                preload="metadata"
                className="w-full h-auto block"
                aria-label={t("login.videoLabel")}
              />
              <button
                type="button"
                onClick={toggleMute}
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white text-xs font-medium px-3 py-2 rounded-full transition-colors"
                aria-label={videoMuted ? t("login.unmuteVideo") : t("login.muteVideo")}
              >
                {videoMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                {videoMuted ? t("login.unmute") : t("login.mute")}
              </button>
            </div>
          </div>

          <p className="text-cyan-200 text-sm mt-5 leading-relaxed">
            {t("login.heroDescription")}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: t("login.featureAI"), sub: t("login.featureAISub") },
            { label: t("login.featureFIAMLA"), sub: t("login.featureFIAMLASub") },
            { label: t("login.featureSecure"), sub: t("login.featureSecureSub") },
          ].map((item) => (
            <div key={item.label} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-white font-semibold text-sm font-manrope">{item.label}</p>
              <p className="text-cyan-300 text-xs mt-1">{item.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      {/* The card, with the links under it (a column, so they never sit
          beside the card on a narrow screen). */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 sm:p-8">
        <div className="w-full max-w-md rounded-2xl p-6 sm:p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
          {/* Mobile logo: the dark-mode one on the dark card (the html
              element can still carry `dark` after signing out). */}
          <div className="flex items-center gap-3 mb-6 sm:mb-8 lg:hidden">
            <img
              src="/logos/logo-light.png"
              alt="Audacia Corporate Services"
              className="h-10 object-contain dark:hidden"
            />
            <img
              src="/logos/logo-dark.png"
              alt="Audacia Corporate Services"
              className="h-10 object-contain hidden dark:block"
            />
          </div>

          {step === "email" && (
            <>
              <div className="mb-6 sm:mb-8">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "#e0f5f7" }}>
                  <Mail className="w-6 h-6" style={{ color: "#171644" }} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 font-manrope">{t("login.signIn")}</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{t("login.enterEmail")}</p>
              </div>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    {t("login.emailLabel")}
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("login.emailPlaceholder")}
                    required
                    data-testid="email-input"
                    className="w-full px-4 py-3 rounded-lg text-base sm:text-sm border outline-none transition-all"
                    style={{ borderColor: "var(--c-border)", background: "var(--c-bg-soft)", color: "var(--c-text-strong)" }}
                    onFocus={(e) => (e.target.style.borderColor = "#171644")}
                    onBlur={(e) => (e.target.style.borderColor = "var(--c-border)")}
                  />
                </div>
                {/* Navy on the dark card needs an outline to read as a button. */}
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  data-testid="email-submit-btn"
                  className="w-full min-h-[44px] flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50 dark:ring-1 dark:ring-inset dark:ring-slate-500"
                  style={{ background: "#171644" }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("login.continue")} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          )}

          {step === "otp" && (
            <>
              <div className="mb-6 sm:mb-8">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "#e0f5f7" }}>
                  <KeyRound className="w-6 h-6" style={{ color: "#171644" }} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 font-manrope">{t("login.enterCode")}</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                  {t("login.codeSent")} <strong>{email}</strong>
                </p>
              </div>
              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <div>
                  <label htmlFor="login-otp" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    {t("login.otpLabel")}
                  </label>
                  <input
                    id="login-otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="otp"
                    data-testid="otp-input"
                    className="w-full px-4 py-3 rounded-lg text-center text-2xl font-mono tracking-[0.5em] border outline-none transition-all"
                    style={{ borderColor: "var(--c-border)", background: "var(--c-bg-soft)", color: "var(--c-text-strong)" }}
                    onFocus={(e) => (e.target.style.borderColor = "#171644")}
                    onBlur={(e) => (e.target.style.borderColor = "var(--c-border)")}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  data-testid="otp-submit-btn"
                  className="w-full min-h-[44px] flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50 dark:ring-1 dark:ring-inset dark:ring-slate-500"
                  style={{ background: "#171644" }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("login.verifySignIn")} <ArrowRight className="w-4 h-4" /></>}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("email"); setOtp(""); }}
                  className="w-full min-h-[44px] text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                >
                  {t("login.differentEmail")}
                </button>
              </form>
            </>
          )}

          {step === "pending" && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "#fffbeb" }}>
                <Shield className="w-8 h-8" style={{ color: "#b45309" }} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 font-manrope mb-3">
                {t("login.pendingTitle")}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-6">
                {t("login.pendingMessage")}
              </p>
              <button
                onClick={() => setStep("email")}
                type="button"
                className="min-h-[44px] px-3 text-sm font-medium transition-colors text-[#171644] dark:text-cyan-300"
              >
                {t("login.backToLogin")}
              </button>
            </div>
          )}

          {step === "timeRestricted" && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "#fef3c7" }}>
                <Clock className="w-8 h-8" style={{ color: "#d97706" }} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 font-manrope mb-3">
                {t("login.timeRestrictedTitle") || "Access Restricted"}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-6">
                {timeRestrictionMsg}
              </p>
              <button
                onClick={() => { setStep("email"); setOtp(""); setTimeRestrictionMsg(""); }}
                type="button"
                className="min-h-[44px] px-3 text-sm font-medium transition-colors text-[#171644] dark:text-cyan-300"
              >
                {t("login.backToLogin")}
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-300/80 mt-6">
          {t("login.poweredBy")}{' '}
          <a
            href="https://audacia.mu"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block py-2 px-1 underline-offset-2 hover:text-white hover:underline transition-colors"
          >
            Audacia
          </a>
          {' · '}
          <Link
            to="/privacy"
            className="inline-block py-2 px-1 underline-offset-2 hover:text-white hover:underline transition-colors"
            data-testid="login-privacy-link"
          >
            {t("privacy.link")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;

